import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../context/authContext";
import { Alert, Box, Button, IconButton, Tooltip, Typography, Divider, CircularProgress } from "@mui/material";
import PageTitle from "../../../components/PageTitle/PageTitle";
import ExpandableDataGrid from "../../../components/ExpandableDataGrid/ExpandableDataGrid";
import SummarizeIcon from "@mui/icons-material/Summarize";
import "../../Services/Services.css";
import { useTranslation } from "react-i18next";
import NotAuthorized from "../../../components/NotAuthorized/NotAuthorized";
import { formatDate } from "../../../utilities/globalServiceUtils";
import Swal from "sweetalert2";
import { getWorkorderReport } from "../../../services/servicesService";
import { useWorkordersData } from "../../../hooks/useServiceRecords";
import DataGridSkeleton from "../../../components/PageSkeletons/DataGridSkeleton";
import DocumentViewModal from "../../../components/DocumentViewModal/DocumentViewModal";
import WorkorderReportsModal from "../../../components/WorkorderReportsModal/WorkorderReportsModal";

const WorkOrders = () => {
    const { hasPermission } = useAuth();
    const { t } = useTranslation();
    const { workorders, workordersLoading, refreshWorkorders } = useWorkordersData();
    const [reportLoadingWo, setReportLoadingWo] = useState(null);
    const [reportLoadingType, setReportLoadingType] = useState(null);
    const [autoExpandWoId, setAutoExpandWoId] = useState(null);
    const [reportListRow, setReportListRow] = useState(null);
    const [reportDocument, setReportDocument] = useState(null);
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // ?open_wo=<wo_number> deep link (CHIP's get_service_workorders_by_site) — auto-expands
    // the matching workorder row once real data has loaded. Mirrors Cases.js's ?open_case=
    // handler exactly (same gate-on-a-real-fetch-having-started rationale — see there).
    const workordersFetchStartedRef = useRef(false);
    useEffect(() => {
        if (workordersLoading) workordersFetchStartedRef.current = true;
    }, [workordersLoading]);

    // Guards against this effect's OWN history.replace (stripping ?open_wo=) re-triggering
    // itself on the resulting location.search change — same pattern as the filter effect below.
    const openWoStripPendingRef = useRef(false);
    // Tracks the SPECIFIC open_wo value already resolved+stripped — same value-based dedup
    // Cases.js's ?open_case= handler uses (openCaseHandledForRef), not a raw location.search
    // string comparison. Live-reproduced 2026-07-22, TWO bugs in sequence on the string-based
    // approach tried first: (1) the deep link opened the row then collapsed it again moments
    // later with no further chat message — the chip:navigate filter listener below
    // unconditionally calls refreshWorkorders() on every chat-driven arrival, which replaces
    // the `workorders` array (a dependency here) and re-runs this effect well after the strip;
    // (2) the string-comparison fix for that then caused the row to never expand at all in
    // some cases. Switching to Cases.js's proven simpler shape instead: this effect has NO
    // "clear when open_wo is absent" branch at all (removed below) — an absent/already-stripped
    // open_wo is just a no-op here, so a `workorders`-only retrigger can never misfire a clear.
    // Collapsing on a genuinely NEW plain "show my work orders" request is handled by the
    // dedicated chip:navigate listener further down instead (driven by the real event, not
    // location-diffing, so it isn't subject to this same retrigger problem).
    const openWoHandledForRef = useRef(null);
    useEffect(() => {
        if (openWoStripPendingRef.current) {
            openWoStripPendingRef.current = false;
            return;
        }
        const params = new URLSearchParams(location.search);
        const openWo = params.get("open_wo");
        if (!openWo) return;
        if (openWoHandledForRef.current === openWo) return;
        // Wait for real data before trying to resolve the match (same gate as before).
        if (!workordersFetchStartedRef.current || workordersLoading) return;
        openWoHandledForRef.current = openWo;

        const normalizedTarget = openWo.trim().toLowerCase();
        const match = (workorders || []).find(
            (w) => String(w?.wo_number || "").trim().toLowerCase() === normalizedTarget
        );
        setAutoExpandWoId(match ? match.id : null);

        // Strip the query param so it can't re-fire on a later re-render or resurrect on
        // back-navigation, same as Cases.js's ?open_case= handler.
        params.delete("open_wo");
        const newSearch = params.toString();
        openWoStripPendingRef.current = true;
        navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, workordersLoading, workorders]);

    // ?filter_wo_type=/?filter_machine=/?filter_booked_resource=/?filter_date_from=/
    // ?filter_date_to= deep-link filters (CHIP's get_service_workorders_by_site) — the chat
    // answer was already correct ("4 installation work orders"), but the page itself kept
    // showing the FULL unfiltered list since nothing here ever read these params. wo_type/
    // machine/booked_resource go through ExpandableDataGrid's initialColumnFilters (the SAME
    // state its own filter-icon UI reads/writes, so it shows as an active filter, not just a
    // silently-narrowed list) — CHIP already resolved these to real, exact column values
    // server-side (comma-separated), so no extra matching is needed here. Date range has no
    // column-filter equivalent on this page at all, so it's applied as a direct pre-filter on
    // the data passed to the grid instead.
    const filterStripPendingRef = useRef(false);
    const [initialColumnFilters, setInitialColumnFilters] = useState(null);
    const [dateRangeFilter, setDateRangeFilter] = useState(null);

    // Applies filters from a raw "?filter_wo_type=...&..." search string. Factored out of the
    // location-based effect below so it can ALSO be driven directly by the "chip:navigate"
    // event (see below) — live-reproduced bug (mirrors the identical one on Cases.js): "show me
    // all installation work orders" then "show work orders for machine X" left the Installation
    // filter stuck active, because the second navigate's destination could textually match what
    // an earlier strip-navigate had already left the URL at — React Router's location doesn't
    // reliably retrigger a location-based effect for that "same string" case (the same
    // "same-route no-op" class already hit by ChatBot.js's refresh_request_list handler). The
    // backend was always correct here (get_service_workorders_by_site's own agent_actions
    // navigate was right); this was purely a frontend delivery gap.
    const applyWorkorderFiltersFromSearch = (searchString) => {
        const params = new URLSearchParams(searchString || "");
        const filterKeys = ["filter_wo_type", "filter_machine", "filter_booked_resource", "filter_date_from", "filter_date_to"];

        // Always set state from THIS request's params — including clearing it back to {}/null
        // when no filter params are present — so a new chat-driven request fully REPLACES the
        // previous one instead of ANDing with a stale filter.
        const columnFilters = {};
        if (params.get("filter_wo_type")) columnFilters.wo_type = params.get("filter_wo_type").split(",").filter(Boolean);
        if (params.get("filter_machine")) columnFilters.machine_id = params.get("filter_machine").split(",").filter(Boolean);
        if (params.get("filter_booked_resource")) columnFilters.booked_resource = params.get("filter_booked_resource").split(",").filter(Boolean);
        setInitialColumnFilters(columnFilters);

        const dateFrom = params.get("filter_date_from");
        const dateTo = params.get("filter_date_to");
        setDateRangeFilter(dateFrom || dateTo ? { from: dateFrom || null, to: dateTo || null } : null);

        return filterKeys.some((k) => params.get(k));
    };

    useEffect(() => {
        if (filterStripPendingRef.current) {
            filterStripPendingRef.current = false;
            return;
        }
        const hadAnyFilterParam = applyWorkorderFiltersFromSearch(location.search);
        if (hadAnyFilterParam) {
            const params = new URLSearchParams(location.search);
            ["filter_wo_type", "filter_machine", "filter_booked_resource", "filter_date_from", "filter_date_to"].forEach((k) => params.delete(k));
            const newSearch = params.toString();
            filterStripPendingRef.current = true;
            navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, location.key]);

    // Fires on EVERY chat-driven navigate to this page, regardless of whether React Router's
    // own location object ends up matching what it already was (see comment above). ChatBot.js
    // dispatches this synchronously with the real destination path BEFORE its own deferred
    // navigate() call, so `detail.path`'s query string is authoritative and doesn't depend on
    // location-diffing timing at all. Applies UNCONDITIONALLY — not just when the destination
    // has no query string — because the same unreliable-retrigger gap isn't limited to
    // "filter -> cleared": live-reproduced (confirmed via direct backend calls, ruling out any
    // server-side bug): "show me all installation work orders" (sets wo_type) followed by "show
    // work orders for machine X" (sets machine_id, no wo_type — the backend's own agent_actions
    // for turn 2 correctly carried ONLY filter_machine) still left the Installation filter
    // active, because that transition ALSO landed on the location-effect's blind spot even
    // though the destination search string wasn't empty. Applying directly here for every
    // chat-driven navigate makes this reliable regardless of which specific filter combination
    // changes; the location-based effect remains for the "arrived at this page fresh from
    // elsewhere" case, where this listener isn't mounted yet to catch the event.
    useEffect(() => {
        const onChipNavigate = (e) => {
            const dest = String(e?.detail?.path || "");
            if (!dest.startsWith("/service/workorders")) return;
            const qIndex = dest.indexOf("?");
            const searchString = qIndex >= 0 ? dest.slice(qIndex) : "";
            applyWorkorderFiltersFromSearch(searchString);
            // useWorkordersData's fetch is keyed ONLY on selectedSite (useServiceRecords.js), so
            // a chat-driven re-ask for the SAME site never retried the data itself — only the
            // filters above. If the underlying fetch ever failed/came back stale (e.g. a startup
            // timing race before site context settled), the page was stuck showing that stale
            // state until selectedSite genuinely changed or a manual reload forced a fresh fetch.
            // Force a real refetch on every chat-driven arrival here too — mirrors Cases.js.
            refreshWorkorders();
        };
        window.addEventListener("chip:navigate", onChipNavigate);
        return () => window.removeEventListener("chip:navigate", onChipNavigate);
        // refreshWorkorders IS a real dep (rebuilt whenever selectedSite changes) — leaving it
        // out would let this listener close over a stale site's refetch function.
    }, [refreshWorkorders]);

    // Same "chip:navigate" event, applied directly for ?open_wo= too (mirrors the filter
    // listener above). Live-reproduced: "Show me WO WO05277" while ALREADY on the Work Orders
    // page never expanded the row — only worked when navigating in from elsewhere — because
    // ChatBot.js's navigate(dest) call above is a same-route no-op that doesn't reliably
    // retrigger the location.search-based effect once this page is already mounted on this
    // route (same "same-route no-op" gap the filter listener above already works around).
    // Idempotent with that effect (just recomputes autoExpandWoId from a given search string),
    // so both firing is harmless redundancy, not a conflict.
    useEffect(() => {
        const onChipNavigateOpenWo = (e) => {
            const dest = String(e?.detail?.path || "");
            if (!dest.startsWith("/service/workorders")) return;
            if (workordersLoading) return;
            const qIndex = dest.indexOf("?");
            const searchString = qIndex >= 0 ? dest.slice(qIndex) : "";
            const openWo = new URLSearchParams(searchString).get("open_wo");
            if (!openWo) {
                setAutoExpandWoId(null);
                return;
            }
            const normalizedTarget = openWo.trim().toLowerCase();
            const match = (workorders || []).find(
                (w) => String(w?.wo_number || "").trim().toLowerCase() === normalizedTarget
            );
            setAutoExpandWoId(match ? match.id : null);
        };
        window.addEventListener("chip:navigate", onChipNavigateOpenWo);
        return () => window.removeEventListener("chip:navigate", onChipNavigateOpenWo);
    }, [workordersLoading, workorders]);

    const displayedWorkorders = useMemo(() => {
        if (!dateRangeFilter) return workorders;
        const from = dateRangeFilter.from ? new Date(dateRangeFilter.from) : null;
        const to = dateRangeFilter.to ? new Date(dateRangeFilter.to) : null;
        if (to) to.setHours(23, 59, 59, 999);
        return (workorders || []).filter((w) => {
            if (!w?.service_start_date) return false;
            const d = new Date(w.service_start_date);
            if (Number.isNaN(d.getTime())) return false;
            if (from && d < from) return false;
            if (to && d > to) return false;
            return true;
        });
    }, [workorders, dateRangeFilter]);

    const columns_wo = [
        { field: "wo_number", headerName: t('TABLE_HEADERS.WO_NUMBER'), flex: 1 },
        { field: "wo_type", headerName: t('TABLE_HEADERS.TYPE'), flex: 1, filterable: true },
        { field: "machine_id", headerName: t('TABLE_HEADERS.MACHINE'), flex: 1, filterable: true },
        { field: "booked_resource", headerName: t('TABLE_HEADERS.BOOKED_RESOURCE', { defaultValue: 'Booked Resource' }), flex: 1, filterable: true },
        // Service Start Date column — commented out per request
        // {
        //     field: "service_start_date", headerName: "Service Start Date", flex: 1, valueFormatter: (value) => formatDate(value),
        //     sortComparator: (v1, v2) => {
        //         const d1 = v1 ? new Date(v1).getTime() : 0;
        //         const d2 = v2 ? new Date(v2).getTime() : 0;
        //         return d1 - d2;
        //     },
        // },
        {
            field: "service_end_date", headerName: "Service End Date", flex: 1, valueFormatter: (value) => formatDate(value),
            sortComparator: (v1, v2) => {
                const d1 = v1 ? new Date(v1).getTime() : 0;
                const d2 = v2 ? new Date(v2).getTime() : 0;
                return d1 - d2;
            },
        },
        // {
        //     field: "created_date", headerName: t('TABLE_HEADERS.CREATED_DATE'), flex: 1, valueFormatter: (value) => formatDate(value),
        //     sortComparator: (v1, v2) => {
        //         const d1 = v1 ? new Date(v1).getTime() : 0;
        //         const d2 = v2 ? new Date(v2).getTime() : 0;
        //         return d1 - d2;
        //     },
        // },
        // {
        //     field: "closed_date", headerName: t('TABLE_HEADERS.CLOSED_DATE'), flex: 1, valueFormatter: (value) => formatDate(value),
        //     sortComparator: (v1, v2) => {
        //         const d1 = v1 ? new Date(v1).getTime() : 0;
        //         const d2 = v2 ? new Date(v2).getTime() : 0;
        //         return d1 - d2;
        //     },
        // },
        {
            field: "actions", headerName: t('TABLE_HEADERS.ACTIONS'), width: 120, headerAlign: "left",
            align: "left", filterable: false, renderCell: (params) => {
                const isService = String(params?.row?.wo_type || "").toLowerCase() === "service";
                const hasReport = Array.isArray(params?.row?.reports) && params.row.reports.length > 0;
                if (!isService || !hasReport) return null;
                return (
                    <Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', height: '100%' }}>
                        <Box sx={{ display: "flex", justifyContent: 'flex-start', alignItems: 'center' }}>
                            <Tooltip title={t('BUTTON.VIEW_SERVICE_REPORT')}>
                                <span>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const reports = params.row.reports || [];
                                            if (reports.length === 1) {
                                                handleViewReport(params.row, reports[0].type, reports[0].id);
                                            } else {
                                                setReportListRow(params.row);
                                            }
                                        }}
                                    >
                                        <SummarizeIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                );
            }
        },
    ];

    const handleViewReport = async (row, reportType, reportId) => {
        try {
            const woNumber = row?.wo_number;
            setReportLoadingWo(woNumber || null);
            setReportLoadingType(reportType || null);
            const resp = await getWorkorderReport(row?.wo_number, reportType, reportId);
            const payload = resp?.data?.data || resp?.data || {};
            const reports = Array.isArray(payload.reports)
                ? payload.reports.filter((report) => report?.url)
                : payload.url ? [{ type: reportType, file_name: "Service Report.pdf", url: payload.url }] : [];

            if (reports.length > 0) {
                const selectedReport = reports.find((report) => String(report.id) === String(reportId))
                    || reports.find((report) => String(report.type).toLowerCase() === String(reportType).toLowerCase())
                    || reports[0];

                setReportDocument({
                    name: selectedReport.file_name || `${woNumber}-${selectedReport.type}.pdf`,
                    url: selectedReport.url,
                    content_type: "application/pdf",
                    attachment_type: "file",
                });
                setReportModalOpen(true);
            } else {
                Swal.fire({
                    icon: 'warning',
                    title: 'Report Not Found',
                    text: 'The requested work order report could not be found.',
                    confirmButtonText: t('GENERAL.OK'),
                });
            }
        } catch (e) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An error occurred while fetching the report. Please try again later.',
                confirmButtonText: t('GENERAL.OK'),
            });
        } finally {
            setReportLoadingWo(null);
            setReportLoadingType(null);
        }
    };

    const handleReportListView = (report) => {
        setReportListRow(null);
        handleViewReport(reportListRow, report.type, report.id);
    };

    // Removed Create New Workorder button
    const canSeeWorkOrders = !!hasPermission('service_management.view_workorders');

    // If user can't see either tab, block access entirely
    if (!canSeeWorkOrders) {
        return <NotAuthorized />;
    }

    if (workordersLoading) {
        return (
            <DataGridSkeleton
                containerClassName="services-container"
                actionButtons={0}
                columns={6}
                rows={6}
            />
        );
    }




    // Ensure valid tab index when permissions change dynamically
    const renderWorkorderMasterDetails = (woRow) => (
        <Box sx={{
            p: 2, width: '100%', border: "1px solid #e6e8eb",
            borderRadius: 2,
        }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body1" fontWeight={"bold"}>Work Order Details: {woRow?.wo_number}</Typography>
                <Typography variant="body2" color="text.secondary">Please review the work order details below</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", columnGap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.WO_NUMBER")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{woRow?.wo_number}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.STATUS")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{woRow?.wo_status}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.TYPE")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{woRow?.wo_type}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.MACHINE")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{woRow?.machine_id || "-"}</Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} flexItem />
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", columnGap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.BOOKED_RESOURCE", { defaultValue: "Booked Resource" })}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{woRow?.booked_resource}</Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} flexItem />
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", columnGap: 2 }}>
                {/* Service Start Date — commented out per request
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">Service Start Date</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{formatDate(woRow?.service_start_date)}</Typography>
                </Box>
                */}
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">Service End Date</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{formatDate(woRow?.service_end_date)}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.CREATED_DATE")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{formatDate(woRow?.created_date)}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.CLOSED_DATE")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{formatDate(woRow?.closed_date) || "-"}</Typography>
                </Box>
            </Box>
            <Divider sx={{ my: 2 }} flexItem />
            <Typography variant="subtitle2">{t("TABLE_HEADERS.DESCRIPTIONS")}</Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>{woRow?.wo_description || "-"}</Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {String(woRow?.wo_type || "").toLowerCase() === "service" && Array.isArray(woRow?.reports) && woRow.reports.length === 1 && woRow.reports.some((report) => report?.type === "service") && (
                    <Button
                        variant="contained"
                        onClick={() => handleViewReport(woRow, "service")}
                        startIcon={<SummarizeIcon />}
                        disabled={reportLoadingWo === woRow?.wo_number}
                    >
                        {reportLoadingWo === woRow?.wo_number && reportLoadingType === "service" ? t("BUTTON.LOADING", { defaultValue: "Loading..." }) : t("BUTTON.VIEW_SERVICE_REPORT")}
                    </Button>
                )}
                {String(woRow?.wo_type || "").toLowerCase() === "service" && Array.isArray(woRow?.reports) && woRow.reports.length === 1 && woRow.reports.some((report) => report?.type === "inspection") && (
                    <Button
                        variant="contained"
                        onClick={() => handleViewReport(woRow, "inspection")}
                        startIcon={<SummarizeIcon />}
                        disabled={reportLoadingWo === woRow?.wo_number}
                    >
                        {reportLoadingWo === woRow?.wo_number && reportLoadingType === "inspection" ? t("BUTTON.LOADING", { defaultValue: "Loading..." }) : "View Inspection Report"}
                    </Button>
                )}
            </Box>
        </Box>
    );

    return (
        <div className="services-container">
            <PageTitle title={t('TITLES.WORK_ORDERS_REPORTS')} />


            {workorders.length === 0 && !workordersLoading ? (
                <Alert severity="info" className="error-alert" sx={{ marginTop: "10px" }}>
                    {t('ALERT_MESSAGES.NO_WORKORDERS_FOUND')}
                </Alert>
            ) : (
                <>
                <ExpandableDataGrid
                    key="workordersGrid"
                    data={displayedWorkorders}
                    columns={columns_wo}
                    pageSize={5}
                    showSearchBar={true}
                    checkboxSelection={false}
                    customButtons={[]}
                    rowIdField="id"
                    sortBy={[{ field: "wo_number", sort: "desc" }]}
                    masterDetails={renderWorkorderMasterDetails}
                    allowMultipleExpand={false}
                    initialExpandedId={autoExpandWoId}
                    initialColumnFilters={initialColumnFilters}
                />
                <WorkorderReportsModal
                    open={!!reportListRow}
                    onClose={() => setReportListRow(null)}
                    woNumber={reportListRow?.wo_number}
                    reports={reportListRow?.reports || []}
                    onView={handleReportListView}
                />
                <DocumentViewModal
                    open={reportModalOpen}
                    setOpen={setReportModalOpen}
                    document={reportDocument}
                    onClose={() => { setReportModalOpen(false); setReportDocument(null); }}
                    displayName={reportDocument?.name}
                />
                </>
            )}
        </div>
    );

};

export default WorkOrders;
